/**
 * Dadverse theme runtime — db-theme.js (deferred, no dependencies)
 *
 * Responsibilities (kept tiny; heavy motion lives in db-motion-*.js):
 *  1. Mirror the capability gate that theme.liquid ran inline before paint.
 *  2. Provide a DOM-first component registry with Shopify Theme Editor
 *     lifecycle (section load/unload/reorder, block select) so every db-*
 *     component initialises once and cleans up after itself.
 *  3. Keep --db-header-h in sync with the rendered header height.
 *
 * Usage inside a component script:
 *   DB.register('proof-rail', (root, ctx) => { ...; return () => cleanup(); });
 * Markup:
 *   <div data-db-component="proof-rail" data-section-id="{{ section.id }}">
 */
(() => {
  const html = document.documentElement;
  const DB = (window.DB = window.DB || {});

  /* ---------------------------------------------------------------------- */
  /* Capability gate (mirrors the inline script in theme.liquid)            */
  /* ---------------------------------------------------------------------- */
  const mq = (q) => (window.matchMedia ? window.matchMedia(q) : { matches: false, addEventListener() {} });
  const reducedMq = mq('(prefers-reduced-motion: reduce)');
  const coarseMq = mq('(pointer: coarse)');

  function readSettings() {
    const el = document.getElementById('db-settings');
    if (!el) return {};
    try {
      return JSON.parse(el.textContent || '{}');
    } catch (e) {
      return {};
    }
  }

  DB.settings = readSettings();

  function computeMotion() {
    const s = DB.settings;
    const saveData = Boolean(navigator.connection && navigator.connection.saveData);
    const lowMemory = typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
    const coarse = coarseMq.matches;
    const level = s.motionLevel || 'cinematic';
    if (reducedMq.matches || level === 'none') return 'reduced';
    if (saveData || lowMemory || level === 'subtle') return 'lite';
    if (coarse && (s.motionTouch || 'lite') === 'lite') return 'lite';
    return 'full';
  }

  DB.capabilities = {
    get reducedMotion() {
      return reducedMq.matches;
    },
    get coarsePointer() {
      return coarseMq.matches;
    },
    get saveData() {
      return Boolean(navigator.connection && navigator.connection.saveData);
    },
    get lowMemory() {
      return typeof navigator.deviceMemory === 'number' && navigator.deviceMemory <= 4;
    },
    get designMode() {
      return Boolean(window.Shopify && window.Shopify.designMode);
    },
  };

  function applyMotion() {
    const mode = computeMotion();
    html.setAttribute('data-db-motion', mode);
    html.setAttribute('data-db-pointer', coarseMq.matches ? 'coarse' : 'fine');
    DB.motion = mode;
    document.dispatchEvent(new CustomEvent('db:motion-change', { detail: { mode } }));
  }

  applyMotion();
  reducedMq.addEventListener && reducedMq.addEventListener('change', applyMotion);

  /* ---------------------------------------------------------------------- */
  /* Component registry + Theme Editor lifecycle                            */
  /* ---------------------------------------------------------------------- */
  const registry = new Map(); // name -> init(root, ctx) => cleanup
  const instances = new WeakMap(); // root -> cleanup

  function mount(root) {
    if (instances.has(root)) return;
    const name = root.dataset.dbComponent;
    const init = registry.get(name);
    if (!init) return;
    try {
      const cleanup = init(root, {
        sectionId: root.dataset.sectionId || root.closest('.shopify-section')?.id?.replace('shopify-section-', ''),
        motion: DB.motion,
        settings: DB.settings,
      });
      instances.set(root, typeof cleanup === 'function' ? cleanup : () => {});
    } catch (error) {
      console.error(`[DB] component "${name}" failed to initialise`, error);
    }
  }

  function unmount(root) {
    const cleanup = instances.get(root);
    if (!cleanup) return;
    try {
      cleanup();
    } catch (error) {
      console.error('[DB] component cleanup failed', error);
    }
    instances.delete(root);
  }

  function scan(scope = document) {
    scope.querySelectorAll('[data-db-component]').forEach(mount);
    if (scope !== document && scope.matches && scope.matches('[data-db-component]')) mount(scope);
  }

  function teardown(scope) {
    if (!scope) return;
    scope.querySelectorAll('[data-db-component]').forEach(unmount);
    if (scope.matches && scope.matches('[data-db-component]')) unmount(scope);
  }

  DB.register = (name, init) => {
    registry.set(name, init);
    // Late registration (script loaded after DOM ready): mount immediately.
    if (document.readyState !== 'loading') {
      document.querySelectorAll(`[data-db-component="${name}"]`).forEach(mount);
    }
  };

  DB.mount = scan;
  DB.unmount = teardown;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan(), { once: true });
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
  document.addEventListener('shopify:section:unload', (event) => teardown(event.target));
  document.addEventListener('shopify:section:reorder', () => {
    // Positions changed; components that measure layout listen for this.
    document.dispatchEvent(new CustomEvent('db:layout-change'));
  });
  document.addEventListener('shopify:block:select', (event) => {
    const root = event.target.closest('[data-db-component]');
    if (root) root.dispatchEvent(new CustomEvent('db:block-select', { detail: event.detail, bubbles: false }));
  });
  document.addEventListener('shopify:block:deselect', (event) => {
    const root = event.target.closest('[data-db-component]');
    if (root) root.dispatchEvent(new CustomEvent('db:block-deselect', { detail: event.detail, bubbles: false }));
  });

  /* ---------------------------------------------------------------------- */
  /* Header height variable (Dawn only sets --header-height when sticky)    */
  /* ---------------------------------------------------------------------- */
  function trackHeader() {
    const header = document.querySelector('.section-header');
    if (!header || !('ResizeObserver' in window)) return;
    const set = () => html.style.setProperty('--db-header-h', `${Math.round(header.getBoundingClientRect().height)}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(header);
  }
  trackHeader();
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.closest && event.target.closest('.section-header')) trackHeader();
  });

  /* ---------------------------------------------------------------------- */
  /* Small utilities shared by db-* scripts                                 */
  /* ---------------------------------------------------------------------- */
  DB.util = {
    debounce(fn, wait = 150) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
      };
    },
    onIdle(fn, timeout = 1500) {
      if ('requestIdleCallback' in window) return requestIdleCallback(fn, { timeout });
      return setTimeout(fn, 1);
    },
    inViewport(el, margin = 0) {
      const r = el.getBoundingClientRect();
      return r.bottom > -margin && r.top < window.innerHeight + margin;
    },
    money(cents) {
      try {
        const fmt = DB.settings.moneyFormat || '${{amount}}';
        const amount = (cents / 100).toFixed(2);
        return fmt.replace(/\{\{\s*amount\s*\}\}/, amount).replace(/\{\{\s*amount_no_decimals\s*\}\}/, Math.round(cents / 100));
      } catch (e) {
        return (cents / 100).toFixed(2);
      }
    },
  };

  document.dispatchEvent(new CustomEvent('db:ready'));
})();
